import React, { useRef } from 'react';

import { BillableViewsAggregations } from '~api/model/dashboardAggregations';

import { formatNumber, message } from '~helper/_common';

import { useShowDetailsTile } from '~hooks/useShowDetailsTile';

import { LivestreamLabel } from '~components/molecules/LivestreamLabel';
import { ReplaysLabel } from '~components/molecules/ReplaysLabel';
import { DataTile } from '~components/organism/DataTile';
import { DetailsTile } from '~components/organism/DetailsTile';
import { ViewsDetailsTile } from '~components/organism/ViewsDetailsTile';
import { ComponentCommonProps } from '~components/types';

export type BillableViewsDataTileProps = ComponentCommonProps & {
    loading: boolean;
    data: BillableViewsAggregations['billableViews'] | null;
    creatorId?: string | null;
    hasDetailsTile?: boolean;
};

export const BillableViewsDataTile: React.FC<BillableViewsDataTileProps> = ({
    className,
    data,
    creatorId,
    loading,
    hasDetailsTile,
    'data-test': dataTest = 'viewsDataTile',
}) => {
    const dataTileRef = useRef(null);
    const { showDetailsTile, setShowDetailsTile } = useShowDetailsTile();

    return (
        <>
            { showDetailsTile && (
                <DetailsTile
                    dataTileRef={ dataTileRef }
                    onClose={ () => setShowDetailsTile(false) }
                    data-test={ `${dataTest}.details` }
                >
                    <ViewsDetailsTile
                        data={ data }
                        creatorId={ creatorId }
                        mode="billableViews"
                        data-test={ `${dataTest}.details.views` } />
                </DetailsTile>
            ) }
            <DataTile
                className={ className }
                contentType="billableViews"
                headline={ message('dataTile.billableViews.headline') }
                headlineSecondary={ message('common.total') }
                mainDataValue={ message('common.views', formatNumber(data?.total || 0)) }
                headlineVariant="h4"
                subHeadlineVariant="h5"
                loading={ loading }
                data-test={ dataTest }
                ref={ dataTileRef }
                onExpandClick={ hasDetailsTile ? () => setShowDetailsTile(true) : undefined }
                chartValues={ data?.history?.map(historyEntry => ({ date: historyEntry.date, value: historyEntry.total })) }>
                <LivestreamLabel
                    loading={ loading }
                    data-test={ `${dataTest}.liveViews` }>
                    { message('common.views', formatNumber(data?.liveViews || 0)) }
                </LivestreamLabel>
                <ReplaysLabel
                    loading={ loading }
                    data-test={ `${dataTest}.replayViews` }>
                    { message('common.views', formatNumber(data?.replayViews || 0)) }
                </ReplaysLabel>
            </DataTile>
        </>
    );
};
